/**
 * Branch → Schedule (landing page).
 *
 * Grid of every active employee at the branch, showing their effective
 * schedule for the currently selected 7-day window. Clicking an employee name
 * opens the 4-week editor. This mirrors the "Employee Schedules" screen from
 * the Mindbody-based system Cocoon is replacing.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const WEEKDAY_LABELS = [
  { key: "mon", short: "Monday" },
  { key: "tue", short: "Tuesday" },
  { key: "wed", short: "Wednesday" },
  { key: "thu", short: "Thursday" },
  { key: "fri", short: "Friday" },
  { key: "sat", short: "Saturday" },
  { key: "sun", short: "Sunday" },
];

// -- Local date helpers (all Bali-local since dates are ISO YYYY-MM-DD) ------
function parseYmd(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}
function fmtDate(dObj) {
  return dObj.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}
function fmtDateFull(dObj) {
  return dObj.toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone: "UTC" });
}
function addDays(dObj, days) {
  const nd = new Date(dObj.getTime());
  nd.setUTCDate(nd.getUTCDate() + days);
  return nd;
}
// Return the Monday (local) of the week containing `dObj`.
function mondayOf(dObj) {
  const dow = dObj.getUTCDay(); // 0=Sun, 1=Mon
  const offset = dow === 0 ? -6 : 1 - dow;
  return addDays(dObj, offset);
}
function toYmd(dObj) {
  return dObj.toISOString().slice(0, 10);
}

// Render one cell — "10:00-18:00" with an italic lunch subline, or a slug.
function ScheduleCell({ day }) {
  if (!day) return <span className="text-neutral-500">—</span>;
  const mode = day.mode;
  if (mode === "day_off" || mode === "off") return <span className="text-neutral-500">- Day Off -</span>;
  if (mode === "training_day") return <span className="text-neutral-500">- Training Day -</span>;
  if (mode === "closed") return <span className="text-neutral-500">- Closed -</span>;
  const work = day.work_start && day.work_end ? `${day.work_start}-${day.work_end}` : "—";
  const lunch = day.lunch_start && day.lunch_end ? `${day.lunch_start}-${day.lunch_end}` : "";
  return (
    <div className="leading-tight">
      <div>{work}</div>
      {lunch && <div className="italic text-neutral-500 text-[11px]">{lunch}</div>}
    </div>
  );
}

export default function BranchSchedule() {
  const { bid } = useParams();
  const [search, setSearch] = useSearchParams();

  // Selected week Monday (ISO YYYY-MM-DD).
  const initialWeek = useMemo(() => {
    const raw = search.get("week");
    if (raw) return raw;
    return toYmd(mondayOf(new Date()));
  }, [search]);
  const [weekStart, setWeekStart] = useState(initialWeek);
  const [data, setData] = useState({ dates: [], employees: [] });
  const [loading, setLoading] = useState(true);

  // Build the "Select Week" options: current + previous + next 3 = 5 windows,
  // each labelled with its first and last date.
  const weekOptions = useMemo(() => {
    const anchor = parseYmd(weekStart);
    const opts = [];
    for (let i = -1; i <= 6; i++) {
      const wk = addDays(anchor, i * 7);
      const end = addDays(wk, 27); // 4-week window per Mindbody UX
      opts.push({
        value: toYmd(wk),
        label: `${fmtDate(wk)} - ${fmtDate(end)}`,
      });
    }
    return opts;
  }, [weekStart]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await staffApi.get(`/branches/${bid}/schedule`, { params: { week_start: weekStart } });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [bid, weekStart]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Keep the URL in sync so a refresh preserves the selected week.
    const cur = search.get("week");
    if (cur !== weekStart) {
      const next = new URLSearchParams(search);
      next.set("week", weekStart);
      setSearch(next, { replace: true });
    }
  }, [weekStart, search, setSearch]);

  const dates = (data.dates || []).map(parseYmd);
  const rangeLabel = dates.length
    ? `${fmtDate(dates[0])} - ${fmtDate(addDays(dates[0], 27))}`
    : "";

  return (
    <StaffShell>
      <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
        <div className="border-b border-neutral-300 pb-4 mb-6" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(230,190,190,0.15) 8px, rgba(230,190,190,0.15) 12px)" }}>
          <h1 className="font-editorial text-3xl md:text-4xl" data-testid="schedule-page-title">
            Employee Schedules: {rangeLabel}
          </h1>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <label className="text-sm text-neutral-700">Select Week:</label>
          <Select value={weekStart} onValueChange={setWeekStart}>
            <SelectTrigger className="w-[220px]" data-testid="schedule-week-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {weekOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-neutral-500 italic ml-4">* Lunch hours are in italic</p>
        </div>

        <div className="text-center mb-3">
          <div className="inline-block font-editorial text-2xl border-b border-neutral-300 px-6 pb-1" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(230,190,190,0.15) 8px, rgba(230,190,190,0.15) 12px)" }}>
            Week 1 : {dates.length ? `${fmtDate(dates[0])} - ${fmtDate(dates[6])}` : ""}
          </div>
        </div>

        <Card className="border-neutral-300 shadow-sm">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-100 text-neutral-700">
                  <th className="text-left px-4 py-2 border-b border-r border-neutral-200 font-medium w-[220px]">Employee</th>
                  <th className="text-center px-2 py-2 border-b border-r border-neutral-200 font-medium w-[120px]">Hours for week</th>
                  {WEEKDAY_LABELS.map((wd, i) => (
                    <th key={wd.key} className="text-center px-2 py-2 border-b border-r border-neutral-200 font-medium">
                      <div className="text-neutral-700">{wd.short}</div>
                      <div className="text-neutral-500 text-xs">{dates[i] ? fmtDateFull(dates[i]) : ""}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="text-center py-8 text-neutral-500">Loading…</td></tr>
                )}
                {!loading && data.employees && data.employees.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-8 text-neutral-500">No employees at this branch.</td></tr>
                )}
                {!loading && data.employees && data.employees.map((emp) => (
                  <tr key={emp.staff_id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`schedule-row-${emp.staff_id}`}>
                    <td className="px-4 py-2 border-r border-neutral-100">
                      <Link
                        to={`/staff/branch/${bid}/schedule/${emp.staff_id}?week=${weekStart}`}
                        className="text-cocoon-forest hover:underline"
                        data-testid={`schedule-employee-link-${emp.staff_id}`}
                      >
                        {emp.full_name}
                      </Link>
                    </td>
                    <td className="px-2 py-2 text-center text-neutral-700 border-r border-neutral-100">{emp.hours_week || 0}</td>
                    {WEEKDAY_LABELS.map((wd) => (
                      <td key={wd.key} className="px-2 py-2 text-center text-[12px] border-r border-neutral-100">
                        <ScheduleCell day={emp.days?.[wd.key]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </StaffShell>
  );
}
